import { subject } from "@casl/ability";
import { useSelector } from "react-redux";

import type { RootState } from "../store/state/rootReducer";

import { useRoleFlags } from "./usePermissions";

const useThemesFlag = () =>
  useSelector((state: RootState) => state.featureFlags?.flags?.enableQualitativeCoding === true);

/** Global (org) themes: system admin or org manager, behind the Themes flag. */
export const useCanManageGlobalThemes = () => {
  const enableThemes = useThemesFlag();
  const { isAdmin, isOrgManager } = useRoleFlags();
  return enableThemes && (isAdmin || isOrgManager);
};

/**
 * Project themes (Themes tab, Hub/Discover coding, auto-detect): system admin, org manager,
 * or project manager of THIS project, behind the Themes flag. The project-manager check is
 * against the project instance: the type-level `isProjectManager` is true for a PM of any
 * project when capabilities were fetched without a projectId.
 */
export const useCanManageProjectThemes = (projectId?: string) => {
  const enableThemes = useThemesFlag();
  const { ability, isAdmin, isOrgManager } = useRoleFlags();
  const isProjectManagerOfProject = Boolean(projectId) && ability.can("update", subject("Project", { id: projectId }));
  return enableThemes && (isAdmin || isOrgManager || isProjectManagerOfProject);
};
